// hooks/useNotification.js
import React, { useState, useEffect } from "react";
import { isElectronRunning } from "../../App";

const useNotification = () => {
  const [permission, setPermission] = useState("default");
  const [error, setError] = useState(null);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    const supported = "Notification" in window || isElectronRunning();
    setIsSupported(supported);

    if (supported) {
      if (isElectronRunning()) {
        setPermission("granted");
      } else {
        setPermission(Notification.permission);
      }
    } else {
      setError("Notifications are not supported in this environment");
    }
  }, []);

  const requestPermission = async () => {
    if (!isSupported) {
      setError("Notifications are not supported");
      return false;
    }

    if (isElectronRunning()) {
      return true;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result === "granted";
    } catch (err) {
      setError("Failed to request notification permission");
      return false;
    }
  };

  const sendNotification = (title, options = {}) => {
    if (!isSupported) {
      setError("Notifications are not supported");
      return;
    }

    if (isElectronRunning()) {
      try {
        window.electronAPI.showNotification(title, options.body || "");
        return;
      } catch (err) {
        setError("Failed to send electron notification");
        return;
      }
    }

    if (permission !== "granted") {
      setError("Notification permission not granted");
      return;
    }

    try {
      const notification = new Notification(title, {
        body: options.body || "",
        icon: options.icon || "",
        badge: options.badge || "",
        tag: options.tag || "default",
        requireInteraction: options.requireInteraction || false,
        silent: options.silent || false,
        data: options.data || {},
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
        options.onClick?.();
      };
    } catch (err) {
      setError("Failed to create notification");
    }
  };

  return {
    isSupported,
    permission,
    requestPermission,
    sendNotification,
    error,
  };
};

const NotificationContext = React.createContext(null);
// new line custom hook

const NotificationProvider = ({ children }) => {
  const notificationService = useNotification();

  return (
    <NotificationContext.Provider value={notificationService}>
      {children}
    </NotificationContext.Provider>
  );
};

export default {
  NotificationProvider,
  useNotification,
};
